import React, {
 useCallback,
 useEffect,
 useState,
} from 'react'
import PropTypes from 'prop-types'
import languages from 'constants/languages'
import {useAuth} from 'context/AuthProvider'
import {DropdownField} from 'helpers/ui-components/form/DropdownField'
import {useTranslation} from 'react-i18next'
import {MenuInner} from './MenuInner'
import HeaderDropdown from './HeaderDropdown'
import {KTSVG} from 'helpers'
import {fetchUserMenu} from 'utils/api/cms/fetchUserConfig'
import {IFormField} from 'models/Common'
import {MenuDropdown} from 'helpers/ui-components/form/MenuItem'
import {MenuButton} from 'helpers/ui-components/form/MenuButton'
import TicketModal from 'module/ticket/components/TicketModal'
import {useNavigate} from 'react-router-dom'
import {commonGetRequest} from 'utils/api/auth/commonService'
import NotificationModal from 'module/notifications/components/NotificationModal'
import {NotificationItem} from 'models/Notification'
import {getNotificationsApi} from 'utils/api/auth/notificationService'
import {USER_CATEGORY} from 'constants/userCategory'

const ROLE_GROUPS = {
 PMU: USER_CATEGORY.PMU_BAYER,
 DISTRIBUTOR: USER_CATEGORY.DISTRIBUTOR_DIRECTOR,
 TSU: USER_CATEGORY.TSU_USER,
} as const
export default function HeaderWrapper() {
 const navigate = useNavigate()
 const {pathname} = window.location
 const {userData, isAuthenticated} = useAuth()
 const [selectedLanguage, setSelectedLanguage] = useState(
  userData?.selectedLanguage ?? 'en'
 )
 const [helpModal, setHelpModal] = useState(false)
 const [showNotifications, setShowNotifications] =
  useState(false)

 const [notifications, setNotifications] = useState<
  NotificationItem[]
 >([])

 const [unreadCount, setUnreadCount] = useState(0)
 const userAccess = localStorage.getItem('user')
 const userCategoryKey = userAccess
  ? JSON.parse(userAccess)?.userCategoryKey
  : null
 const {t, i18n} = useTranslation()
 const hideMenuRoutes = ['/all-tickets', '/faq']

 const isTicketRoute = hideMenuRoutes.some((route) =>
  pathname.startsWith(route)
 )
 const changeLanguageHandler = (lang: string) => {
  i18n.changeLanguage(lang)
 }
 // let history = useHistory()

 const handleChangeLanguage = async (language: string) => {
  if (language) {
   setSelectedLanguage(language)
   let menu: IFormField = {}
   if (userData?.userCategoryKey) {
    menu = await fetchUserMenu(
     userData?.userCategoryKey,
     language
    )
   }

   localStorage.setItem(
    'user',
    JSON.stringify({
     ...userData,
     menu,
     selectedLanguage: language,
    })
   )
   changeLanguageHandler(language)
   window.location.reload()
  }
 }

 const handleFaqClick = async () => {
  const res = await commonGetRequest<string | null>(
   '/common/v1/faqs?app_code=web'
  )

  if (res) {
   navigate('/faq', {state: {url: res}})
  }
 }

 const fetchNotifications = useCallback(async () => {
  const res = await getNotificationsApi({
   app_id: 'web',
   status: 'all',
   days: 30,
   page: 1,
   page_size: 10,
  })

  if (res) {
   setNotifications(res.results)
  }
 }, [])

 const fetchUnreadCount = useCallback(async () => {
  const res = await getNotificationsApi({
   app_id: 'web',
   status: 'unread',
   days: 30,
   page: 1,
   page_size: 10,
  })

  if (res) {
   setUnreadCount(res.count)
  }
 }, [])

 useEffect(() => {
  if (!isAuthenticated) return

  void fetchUnreadCount()

  const interval = setInterval(() => {
   if (document.visibilityState === 'visible') {
    void fetchUnreadCount()
   }
  }, 8000)

  const handleVisibilityChange = () => {
   if (document.visibilityState === 'visible') {
    void fetchUnreadCount()
   }
  }

  document.addEventListener(
   'visibilitychange',
   handleVisibilityChange
  )

  return () => {
   clearInterval(interval)
   document.removeEventListener(
    'visibilitychange',
    handleVisibilityChange
   )
  }
 }, [isAuthenticated, fetchUnreadCount])

 useEffect(() => {
  if (!isAuthenticated || !showNotifications) return

  if (document.visibilityState !== 'visible') return

  void fetchNotifications()

  const interval = setInterval(() => {
   if (document.visibilityState === 'visible') {
    void fetchNotifications()
   }
  }, 8000)

  const handleVisibilityChange = () => {
   if (
    document.visibilityState === 'visible' &&
    showNotifications
   ) {
    void fetchNotifications()
   }
  }

  document.addEventListener(
   'visibilitychange',
   handleVisibilityChange
  )

  return () => {
   clearInterval(interval)
   document.removeEventListener(
    'visibilitychange',
    handleVisibilityChange
   )
  }
 }, [
  isAuthenticated,
  showNotifications,
  fetchNotifications,
 ])

 useEffect(() => {
  changeLanguageHandler(selectedLanguage)
 }, [])

 return (
  <>
   {/* Navbar */}
   <nav
    className={`right-0 w-inherit bg-white header sm:px-2`}
   >
    <div className='md:flex-row md:flex-nowrap md:justify-between flex items-center py-2 container'>
     <div className='flex items-center'>
      <KTSVG
       path='/media/icons/Farmrise-Logo.svg'
       svgClassName='md:w-[88px] h-[33px] sm:w-[50px]'
      />
      <hr className='bg-[#E6EBF0] w-[1px] h-[48px] mx-2' />
      <div className='w-full'>
       <div className='text-[10px] font-normal text-[#5A6066] mb-[4px]'>
        {t('header.foundedBy')}
       </div>
       <div className='md:flex-row md:flex-nowrap md:justify-between flex  align-items-center'>
        <KTSVG
         className='mr-2'
         path='/media/icons/bayer.svg'
         svgClassName='md:w-[26px] md:h-[26px] sm:w-[26px] sm:w-[26px]'
        />
        <KTSVG
         path='/media/icons/Rabo-Logo.svg'
         svgClassName='md:w-[75px] h-[26px] sm:w-[50px]'
        />
       </div>
      </div>
      <hr className='bg-[#E6EBF0] w-[1px] h-[48px] mx-2' />
      <div className='w-full'>
       <div className='text-[10px] font-normal text-[#5A6066] mb-[4px]'>
        {t('header.poweredBy')}
       </div>
       <KTSVG
        path='/media/icons/logo.svg'
        svgClassName='w-[62px] h-[26px]'
       />
      </div>
     </div>
     <div className='w-auto mx-autp items-center flex md:justify-between sm:justify-end md:flex-nowrap flex-wrap md:pl-10 '>
      {/* Form */}
      {pathname === '/dashboard' ? (
       <form className='md:flex hidden flex-row flex-wrap items-center lg:ml-auto mr-3'>
        <div className='relative flex w-full flex-wrap items-stretch'>
         <span className='z-10 h-full leading-snug font-normal absolute text-center text-blueGray-300 bg-transparent rounded text-base items-center justify-center w-8 pl-3 py-3'>
          <i className='fas fa-search'></i>
         </span>
         <input
          type='text'
          placeholder='Search here...'
          className='border-0 px-4 py-3 md:w-56 placeholder-rose-300 text-blueGray-600 relative lg:block md:hidden  bg-white rounded text-sm shadow outline-none focus:outline-none focus:ring w-full'
         />
        </div>
       </form>
      ) : (
       ''
      )}

      <MenuDropdown
       trigger={
        <MenuButton
         icon='/media/icons/help.svg'
         label='Help'
        />
       }
       items={[
        {
         label: t('help.allTickets'),
         iconPath: '/media/icons/allTickets.svg',
         onClick: () => navigate('/all-tickets'),
        },
        ...(userCategoryKey !== ROLE_GROUPS.TSU
         ? [
            {
             label: t('help.raiseTicket'),
             iconPath: '/media/icons/raiseTicket.svg',
             onClick: () => setHelpModal(true),
             divider: true,
            },
           ]
         : []),
        {
         label: 'FAQ',
         iconPath: '/media/icons/faq.svg',
         onClick: handleFaqClick,
         divider: true,
        },
        {
         label: t('help.tutorials'),
         iconPath: '/media/icons/tutorial.svg',
         onClick: () => {
          navigate('/tutorials')
         },
        },
       ]}
      />
      {helpModal && (
       <TicketModal
        open={true}
        onClose={() => {
         setHelpModal(false)
        }}
       />
      )}
      <DropdownField
       label={t('common.changeLanguage')}
       labelClassName='text-xs font-normal text-[#5A6066] !pb-0'
       className='relative m-0 block text-black text-xs w-[80px] border-0 bg-white px-1 font-semibold'
       wrapperClassName='flex items-center'
       inputWrapperClassName='!rounded-[0] !border-0 flex items-center sm:pr-0 md:pr-4'
       required={false}
       data={languages}
       id={`language`}
       name={`language`}
       value={selectedLanguage}
       onChange={(e) => {
        void handleChangeLanguage(e.target.value)
       }}
       OptionValueKey='locale'
       OptionLabelKey='name'
       placeholder={t('common.language')}
      />
      <div className='flex items-center'>
       <div className='mx-4 h-[48px] w-[1px] bg-[#E6EBF0]' />

       <div
        className='relative cursor-pointer px-2'
        onClick={() =>
         setShowNotifications((prev) => !prev)
        }
       >
        {unreadCount > 0 && (
         <div className='absolute -top-1 right-0 z-10 bg-[#D92D20] text-white text-[10px] font-semibold min-w-[16px] h-[16px] rounded-full flex items-center justify-center px-1 shadow'>
          {unreadCount}
         </div>
        )}

        <KTSVG
         path='/media/icons/bell_icon.svg'
         svgClassName='w-[26px] h-[26px]'
        />
       </div>

       <div className='mx-4 h-[48px] w-[1px] bg-[#E6EBF0]' />
      </div>
      <NotificationModal
       open={showNotifications}
       onClose={() => setShowNotifications(false)}
       notifications={notifications}
       refreshNotifications={fetchNotifications}
       unreadCount={unreadCount}
      />
      {!isAuthenticated && (
       <a
        href='/login'
        className='text-[#4E83E4] text-base font-semibold underline underline-offset-4 md:ml-1 md:block lg:block sm:hidden'
       >
        {t('header.login')}
       </a>
      )}
      {/* User */}
      <ul className='flex-col md:flex-row list-none items-center hidden md:flex'>
       {isAuthenticated && <HeaderDropdown />}
      </ul>
     </div>
    </div>
    {isAuthenticated && !isTicketRoute && (
     <div className='bg-[#ECF3FF]'>
      <div className='bg-[#ECF3FF] md:justify-center flex items-center py-3 container'>
       <MenuInner />
      </div>
     </div>
    )}
   </nav>
   {/* End Navbar */}
  </>
 )
}

HeaderWrapper.defaultProps = {
 hubName: 'Unified Agricultural Platform',
}

HeaderWrapper.propTypes = {
 hubName: PropTypes.string,
}
